import React, { useEffect, useState } from "react";
import { StyleSheet } from "react-native";
import i18n from "utils/i18n";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import _ from "lodash"
import Form, { createTimeOptions } from "components/molecules/Form";
import { FormFieldsConstants, FormStatus } from 'utils/constants/form-constants';
import ThemedBlock from "components/atoms/ThemedBlock";
import { settingsConstants, settingsOptions } from "utils/constants/settings-constants";
import HelperSetting from "utils/helper-setting";
import HelperString from "utils/helper-string";
import GraphqlBuilder from "utils/request/graphql/builder";
import MutationManagementSettings from "graphql/mutations/management/MutationManagementSettings";
import FetchCachedSettings from "utils/fetch/cached/FetchCachedSettings";
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants } from "utils/constants/management-right-constants";
import { useShowHamburger } from 'utils/helper-device'
import HelperAlerts from 'utils/HelperAlerts'
import LoadingComponent from 'components/atoms/Loading'
import { scanThroughConstants } from 'utils/constants/scan-constants'
import { privacyOptionsSelect } from 'utils/constants/PrivacyOptionsConstants'
import isEqual from 'react-fast-compare'

const ManagementSettingsAppScene = () => {
	const showHamburger = useShowHamburger();
	
	const [ loading, setLoading ] = useState( true )
	const [ settingValues, setSettingValues ] = useState( {} )
	const [ settingFields, setSettingFields ] = useState( {} )
	const [ formKey, setFormKey ] = useState( HelperString.random() )
	
	const createSettingFields = ( values ) => {
		if( !values ) {
			values = settingValues
		}
		
		let fields = [];
		
		// Basic options
		fields = [
			...fields,
			...[
				{
					label: i18n.t(`management:form.settings.titles.basics`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.COMPANY_NAME,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.COMPANY_NAME }.title`),
					type: FormFieldsConstants.TEXT,
				},
				{
					name: settingsConstants.EMAIL_TLD,
					required: false,
					label: i18n.t(`management:form.settings.${settingsConstants.EMAIL_TLD }.title`),
					type: FormFieldsConstants.TEXT,
				},
				{
					name: settingsConstants.LOG_IN_QR_CODE,
					required: false,
					label: i18n.t(`management:form.settings.${settingsConstants.LOG_IN_QR_CODE }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				}
			]
		];
		
		// Profile
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.profile`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.PROFILE_EDITING,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PROFILE_EDITING }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		];
		
		if( values[ settingsConstants.PROFILE_EDITING ] === settingsOptions.YES  ) {
			fields = [
				...fields,
				{
					name: settingsConstants.PROFILE_ENABLE_PHOTO_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_PHOTO_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_NAME_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_NAME_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_PHONENUMBER_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_PHONENUMBER_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_LANDLINE_PHONENUMBER_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_LANDLINE_PHONENUMBER_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_JOBTITLE_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_JOBTITLE_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_TEAM_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_TEAM_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_DEPARTMENT_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_DEPARTMENT_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_ERO_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_ERO_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PROFILE_ENABLE_ACCESS_CARD_ID_EDITING,
					required: true,
					label: i18n.t( `management:form.settings.${ settingsConstants.PROFILE_ENABLE_ACCESS_CARD_ID_EDITING }.title` ),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		}
		
		// Commute
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.commute`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.COMMUTE_ENABLE,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.COMMUTE_ENABLE }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				}
			]
		]
		
		// Workday
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.workday`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.WORKDAY_EDITING,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.WORKDAY_EDITING }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		];

		// Credits
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.credits`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.CREDITS_ENABLED,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.CREDITS_ENABLED }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		];
		
		// Privacy
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.privacy`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.PRIVACY_EDITING,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PRIVACY_EDITING }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PRIVACY_VISIBLE_FOR_EMPLOYEES_DEFAULT,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PRIVACY_VISIBLE_FOR_EMPLOYEES_DEFAULT }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.PRIVACY_SHOW_PHONENUMBER,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PRIVACY_SHOW_PHONENUMBER }.title`),
					type: FormFieldsConstants.SELECT,
					options: privacyOptionsSelect
				},
				{
					name: settingsConstants.PRIVACY_SHOW_LANDLINE_PHONENUMBER,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PRIVACY_SHOW_LANDLINE_PHONENUMBER }.title`),
					type: FormFieldsConstants.SELECT,
					options: privacyOptionsSelect
				},
				{
					name: settingsConstants.PRIVACY_SHOW_CALENDAR,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PRIVACY_SHOW_CALENDAR }.title`),
					type: FormFieldsConstants.SELECT,
					options: privacyOptionsSelect
				},
				{
					name: settingsConstants.PRIVACY_SHOW_FAVOURITE,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.PRIVACY_SHOW_FAVOURITE }.title`),
					type: FormFieldsConstants.SELECT,
					options: privacyOptionsSelect
				}
			]
		]
		
		const calendarMinDateOptions = _.map( [ 'week:0', 'week:1', 'week:2', 'week:3', 'month:1', 'month:2', 'month:3', 'month:4', 'month:5', 'month:6'], week => {
			return createTimeOptions(week);
		} );
		
		const calendarMaxDateOptions = _.map( ['week:1', 'week:2', 'week:3', 'month:1', 'month:2', 'month:3', 'month:4', 'month:5', 'month:6'], week => {
			return createTimeOptions(week);
		} );
		
		// Options for bookings
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.bookings`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.CALENDAR_MIN_DATE,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.CALENDAR_MIN_DATE }.title`),
					type: FormFieldsConstants.SELECT,
					options: calendarMinDateOptions
				},
				{
					name: settingsConstants.CALENDAR_MAX_DATE,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.CALENDAR_MAX_DATE }.title`),
					type: FormFieldsConstants.SELECT,
					options: calendarMaxDateOptions
				},
				{
					name: settingsConstants.BOOKING_ENABLE_PARKING,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.BOOKING_ENABLE_PARKING }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		]
		
		if( values[ settingsConstants.BOOKING_ENABLE_PARKING ] === settingsOptions.YES  ) {
			fields = [
				...fields,
				...[
					{
						name: settingsConstants.BOOKING_ENABLE_PARKING_LICENSE_PLATE,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.BOOKING_ENABLE_PARKING_LICENSE_PLATE }.title`),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
				]
			]
		}
		
		fields = [
			...fields,
			...[
				{
					name: settingsConstants.BOOKING_ENABLE_LUNCH,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.BOOKING_ENABLE_LUNCH }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
				{
					name: settingsConstants.BOOKING_ENABLE_REASON,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.BOOKING_ENABLE_REASON }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				}
			]
		];
		
		// Presence
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.presence`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.SCAN_IN_ENABLED,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.SCAN_IN_ENABLED }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				}
			]
		]
		
		// We use get to also take care of 'default'
		if( values[ settingsConstants.SCAN_IN_ENABLED ] === settingsOptions.YES  ) {
			fields = [
				...fields,
				...[
					{
						name: settingsConstants.SCAN_IN_TYPE,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.SCAN_IN_TYPE }.title`),
						type: FormFieldsConstants.SELECT,
						options: [
							{ value: scanThroughConstants.QRCODE, label: i18n.t( `form.fields.scan_type.${scanThroughConstants.QRCODE}`) },
							{ value: scanThroughConstants.BUTTON, label: i18n.t( `form.fields.scan_type.${scanThroughConstants.BUTTON}`) }
						],
						style: styles.subfield
					},
					{
						name: settingsConstants.BOOKING_ENABLE_TRANSPORT_SELECT,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.BOOKING_ENABLE_TRANSPORT_SELECT }.title`),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.SCAN_ENABLE_PRESENCE_METER,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.SCAN_ENABLE_PRESENCE_METER }.title`),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.SCAN_ENABLE_TIME,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.SCAN_ENABLE_TIME }.title`),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.SCAN_OUT_ENABLED,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.SCAN_OUT_ENABLED }.title`),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
				]
			];
		}
		
		if( values[ settingsConstants.SCAN_OUT_ENABLED ] === settingsOptions.YES  ) {
			fields = [
				...fields,
				...[
					{
						name: settingsConstants.SCAN_OUT_TYPE,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.SCAN_OUT_TYPE }.title`),
						type: FormFieldsConstants.SELECT,
						options: [
							{ value: scanThroughConstants.QRCODE, label: i18n.t( `form.fields.scan_type.${scanThroughConstants.QRCODE}`) },
							{ value: scanThroughConstants.BUTTON, label: i18n.t( `form.fields.scan_type.${scanThroughConstants.BUTTON}`) }
						],
						style: styles.subfield
					},
				]
			]
		}
		
		// Teams
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.teams`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.ENABLE_TEAMS,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.ENABLE_TEAMS }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				}
			]
		];
		
		// Options for screens
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.coworkers`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.COWORKER_ENABLE_SCREEN,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.COWORKER_ENABLE_SCREEN }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				}
			]
		];
		
		// We use get to also take care of 'default'
		if( values[ settingsConstants.COWORKER_ENABLE_SCREEN ] === settingsOptions.YES  ) {
			fields = [
				...fields,
				...[
					{
						name: settingsConstants.COWORKER_ENABLE_SEARCH,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_ENABLE_SEARCH}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_ENABLE_LINK_SHARE,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_ENABLE_LINK_SHARE}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_ENABLE_DIRECT_SHARE,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_ENABLE_DIRECT_SHARE}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_CALENDER_ONLY_FROM_TEAM,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_CALENDER_ONLY_FROM_TEAM}.title` ),
						type: FormFieldsConstants.SELECT,
						options: [
							{ value: settingsOptions.YES, label: i18n.t( 'management:ui.teams' ) },
							{ value: settingsOptions.NO, label: i18n.t( 'management:ui.company' ) }
						],
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_FAVOURITE_ONLY_FROM_TEAM,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_FAVOURITE_ONLY_FROM_TEAM}.title` ),
						type: FormFieldsConstants.SELECT,
						options: [
							{ value: settingsOptions.YES, label: i18n.t( 'management:ui.teams' ) },
							{ value: settingsOptions.NO, label: i18n.t( 'management:ui.company' ) }
						],
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_SHOW_EMAILADDRESS,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SHOW_EMAILADDRESS}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_SHOW_PHONENUMBER,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SHOW_PHONENUMBER}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_PHONENUMBER_ONLY_FROM_TEAM,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_PHONENUMBER_ONLY_FROM_TEAM}.title` ),
						type: FormFieldsConstants.SELECT,
						options: [
							{ value: settingsOptions.YES, label: i18n.t( 'management:ui.teams' ) },
							{ value: settingsOptions.NO, label: i18n.t( 'management:ui.company' ) }
						],
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_SHOW_LANDLINE_PHONENUMBER,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SHOW_LANDLINE_PHONENUMBER}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subfield
					},
					{
						label: i18n.t(`management:form.settings.titles.coworkers_search`),
						type: FormFieldsConstants.TITLE,
						style: styles.subfield
					},
					{
						name: settingsConstants.COWORKER_SEARCH_COMPANY,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SEARCH_COMPANY}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subsubfield
					},
					{
						name: settingsConstants.COWORKER_SEARCH_DEPARTMENT,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SEARCH_DEPARTMENT}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subsubfield
					},
					{
						name: settingsConstants.COWORKER_SEARCH_TEAM,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SEARCH_TEAM}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subsubfield
					},
					{
						name: settingsConstants.COWORKER_SEARCH_JOBTITLE,
						required: true,
						label: i18n.t( `management:form.settings.${settingsConstants.COWORKER_SEARCH_JOBTITLE}.title` ),
						type: FormFieldsConstants.SELECT_ACTIVE,
						style: styles.subsubfield
					},
				]
			];
		}
		
		// Other options
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.ero`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.ERO_ENABLE,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.ERO_ENABLE }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		];
		
		if( values[ settingsConstants.ERO_ENABLE ] === settingsOptions.YES  ) {
			fields = [
				...fields,
				...[
					{
						name: settingsConstants.EMERGENCY_NUMBER,
						required: true,
						label: i18n.t(`management:form.settings.${settingsConstants.EMERGENCY_NUMBER }.title`),
						type: FormFieldsConstants.TEXT,
					},
				]
			];
		}
		
		// Other options
		fields = [
			...fields,
			...[
				{ type: FormFieldsConstants.DIVIDER },
				{
					label: i18n.t(`management:form.settings.titles.confidant`),
					type: FormFieldsConstants.TITLE
				},
				{
					name: settingsConstants.CONFIDANT_ENABLE,
					required: true,
					label: i18n.t(`management:form.settings.${settingsConstants.CONFIDANT_ENABLE }.title`),
					type: FormFieldsConstants.SELECT_ACTIVE,
				},
			]
		];
		
		return fields;
	}
	
	const changedForm = ( formValues, validationResult ) => {
		
		const newSettingFields = createSettingFields( formValues );
		
		if( !isEqual( settingFields, newSettingFields ) ) {
			setSettingFields( newSettingFields )
		}
	}
	
	const saveForm = ( values, { callback } = {} ) => {
		// Make sure some settings are set to a proper value when left out (for instance for 'sub options')
		
		// Checkin disabled: No transport and no check-out
		if ( values[ settingsConstants.SCAN_IN_ENABLED ] === settingsOptions.NO )
		{
			values[ settingsConstants.SCAN_OUT_ENABLED ] = settingsOptions.NO;
			values[ settingsConstants.BOOKING_ENABLE_TRANSPORT_SELECT ] = settingsOptions.NO;
		}
		
		if ( values[ settingsConstants.COWORKER_ENABLE_SCREEN ] === settingsOptions.NO )
		{
			values[ settingsConstants.COWORKER_ENABLE_LINK_SHARE ] = settingsOptions.NO;
			values[ settingsConstants.COWORKER_ENABLE_DIRECT_SHARE ] = settingsOptions.NO;
		}
		
		new GraphqlBuilder()
			.addQuery( 'mutation', MutationManagementSettings, {settings_json: JSON.stringify( values ) } )
			.setOnStart( () => {
				callback( FormStatus.LOADING )
			} )
			.setOnSuccess( ( result ) => {
				callback( FormStatus.SUCCESS )
				reloadSettings()
			} )
			.setOnError( ( result ) => {
				callback( FormStatus.ERROR )
			} )
			.execute()
	}
	
	const reloadSettings = () => {
		FetchCachedSettings.reload( {
			callbackSuccess: ( values ) => {
				HelperAlerts.alert( '', i18n.t('management:pages.settings.index.reload_takes_time') )
				changedForm( values )
			}
		});
	}
	
	useEffect( () => {
		let values = {}
		_.forEach( settingsConstants, ( settingString ) => {
			values[ settingString ] = HelperSetting.get( settingString );
		})
		
		changedForm( values, {} );
		setSettingValues( values )
		setLoading( false )
	}, [] )
	
	let content = <LoadingComponent />
	if( !loading ) {
		content = (
			<ThemedBlock
				header={ i18n.t('management:form_block.title_default') }
				shadow
				mediumPadding>
				
				<Form
					key={ HelperString.hash( 'form', formKey ) }
					values={ settingValues }
					fields={ settingFields }
					onChange={ changedForm.bind( this ) }
					onSubmit={ saveForm.bind( this ) }
					forceSaveButton={ true }
					disabled={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.SETTINGS )}
				/>
			</ThemedBlock>
		)
	}

	return (
		<ThemedSafeAreaComponentTop
			top={ false }>

			<ThemedLayout
				title={i18n.t('management:pages.settings.index.title')}
				topNavigation={true}
				topNavigationProps={{
					showHamburgerDrawer: showHamburger
				}}>
				
				{ content }
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

const styles = StyleSheet.create({
	column: {
		padding: 20
	},
	subfield: {
		paddingLeft: 20
	},
	subsubfield: {
		paddingLeft: 40
	}
});

export default ManagementSettingsAppScene
